import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useRef } from "react";
import { useUnmount } from "react-use";
import { SampleProcessingState } from "../../samples_cloud/enums";
import { AnalysisStatus } from "../@types";
import api from "../api";

export const URL_ANALYSIS_STATUS = "urlAnalysisStatus";

const REFETCH_INTERVAL = 10 * 1000; // 10 seconds

export const getIsAnalyzing = (state?: string) =>
  SampleProcessingState.PROCESSING.toLowerCase() === state?.toLowerCase() ||
  SampleProcessingState.UNPROCESSED.toLowerCase() === state?.toLowerCase();

const useUrlAnalysisStatus = (url: string) => {
  const queryClient = useQueryClient();
  const previousAnalysisStatus = useRef<string | undefined>(undefined);
  const abortControllerRef = useRef<AbortController | null>(null);

  useUnmount(() => {
    abortControllerRef.current?.abort();
  });

  const fetch = () => {
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const previousData = queryClient.getQueryData<
      AxiosResponse<AnalysisStatus>
    >([URL_ANALYSIS_STATUS, url]);

    previousAnalysisStatus.current = previousData?.data?.analysis_status;

    return api.getAnalysisStatus(url, abortController.signal);
  };

  const { data, refetch } = useQuery({
    queryKey: [URL_ANALYSIS_STATUS, url],
    queryFn: fetch,
    enabled: Boolean(url),
    select: ({ data: response }) => response,
    refetchInterval: ({ state }) => {
      const { data: { data: response = undefined } = {} } = state ?? {};
      if (!getIsAnalyzing(response?.analysis_status)) return false;
      return REFETCH_INTERVAL;
    }
  });

  const isFinished =
    previousAnalysisStatus.current &&
    getIsAnalyzing(previousAnalysisStatus.current) &&
    !getIsAnalyzing(data?.analysis_status);

  const isAnalyzing = getIsAnalyzing(data?.analysis_status);

  return { refetch, isFinished, isAnalyzing };
};

export default useUrlAnalysisStatus;
